import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { api, formatDateShort } from "@/lib/api";
import { PHASES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import PageHeading from "@/components/PageHeading";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const firstName = (user?.name || "").split(" ")[0];

  if (!data) return null;

  return (
    <div data-testid="dashboard-page">
      <PageHeading kicker="01 · PANORAMICA" title={`Ciao, ${firstName}.`} subtitle="Tutto quello che sta succedendo sui tuoi brand, a colpo d'occhio." />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10 mb-12">
        <div className="bg-greige/60 p-6">
          <p className="font-display text-4xl text-ink">{data.brands_count}</p>
          <p className="meta-label mt-2">BRAND ATTIVI</p>
        </div>
        <div className="bg-greige/60 p-6">
          <p className="font-display text-4xl text-ink">{Object.values(data.tasks_by_phase || {}).reduce((a, b) => a + b, 0)}</p>
          <p className="meta-label mt-2">TASK TOTALI</p>
        </div>
        <div className="bg-greige/60 p-6 col-span-2 md:col-span-1">
          <p className="font-display text-4xl text-ink">{data.overdue_tasks?.length || 0}</p>
          <p className="meta-label mt-2">TASK IN RITARDO</p>
        </div>
      </div>

      <div className="mb-12">
        <p className="meta-label mb-4">PIPELINE TASK</p>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {PHASES.map((p) => (
            <div key={p.n} className="flex items-center gap-2.5">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="font-body text-sm text-ink">{data.tasks_by_phase?.[String(p.n)] ?? 0}</span>
              <span className="font-mono2e text-[0.62rem] text-stone2e">{p.label.toUpperCase()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <p className="meta-label mb-4">TASK IN RITARDO</p>
          {(data.overdue_tasks || []).length === 0 ? (
            <p className="font-editorial italic text-stone2e">Nessuna task in ritardo. Ottimo lavoro.</p>
          ) : (
            <div className="space-y-px bg-black/10 border border-black/10">
              {data.overdue_tasks.map((t) => (
                <Link to="/task" key={t.id} data-testid={`overdue-task-${t.id}`} className="bg-greige/60 hover:bg-white/70 transition-colors p-4 flex items-center justify-between gap-3 border-l-2 border-l-black">
                  <div className="flex items-center gap-3 min-w-0">
                    <AlertTriangle size={16} strokeWidth={1.75} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="font-body text-sm text-ink truncate">{t.title} · {t.item_title}</p>
                      <p className="meta-label mt-0.5">{t.brand_name?.toUpperCase()}</p>
                    </div>
                  </div>
                  <span className="font-mono2e text-xs shrink-0">{formatDateShort(t.due_date).toUpperCase()}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="meta-label mb-4">PROSSIME PUBBLICAZIONI</p>
          {(data.upcoming_items || []).length === 0 ? (
            <p className="font-editorial italic text-stone2e">Nessun contenuto in calendario.</p>
          ) : (
            <div className="space-y-px bg-black/10 border border-black/10">
              {data.upcoming_items.map((i) => (
                <Link to="/calendario" key={i.id} data-testid={`upcoming-item-${i.id}`} className="bg-greige/60 hover:bg-white/70 transition-colors p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-body text-sm text-ink truncate">{i.title}</p>
                    <p className="meta-label mt-0.5">{i.brand_name?.toUpperCase()} · {i.format?.toUpperCase()}</p>
                  </div>
                  <span className="font-mono2e text-xs text-stone2e shrink-0">{formatDateShort(i.publish_date).toUpperCase()}</span>
                </Link>
              ))}
            </div>
          )}

          <p className="meta-label mb-4 mt-10">FEEDBACK RECENTI DAI CLIENTI</p>
          {(data.recent_feedback || []).length === 0 ? (
            <p className="font-editorial italic text-stone2e">Ancora nessun feedback.</p>
          ) : (
            <div className="space-y-3">
              {data.recent_feedback.map((f) => (
                <div key={f.id} className="flex items-start gap-3">
                  <ArrowUpRight size={15} strokeWidth={1.5} className="text-stone2e mt-0.5 shrink-0" />
                  <p className="font-body text-sm text-ink">
                    <span className="font-medium">{f.user_name}</span>{" "}
                    {f.type === "approvazione" ? "ha approvato un contenuto" : `ha lasciato una nota: «${f.text}»`}
                    <span className="meta-label ml-2">{f.brand_name?.toUpperCase()}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
